import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Compass, Heart, Sparkles } from 'lucide-react';
import { values, images, clients, readingList } from '../mock';

const About = () => {
  return (
    <main className="pt-32">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">Who we are</div>
            <h1 className="font-serif-display text-6xl md:text-8xl text-[#1E3A2B] mt-6 leading-[0.98] tracking-[-0.02em]">
              A practice<br /> for insight,<br /> behaviour, and <span className="italic text-[#6B8E4E]">sustained change</span>.
            </h1>
          </div>
        </div>
      </section>

      {/* Purpose block */}
      <section className="mt-24 md:mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-[28px] overflow-hidden">
              <img src={images.byron} alt="Byron Batten - Director" className="w-full h-full object-cover image-zoom" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-8">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">Purpose · People</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#1E3A2B] mt-6 leading-[1.02]">
              Meet Byron
            </h2>
            <div className="mt-8 space-y-5 text-[16px] text-[#3d4a41] leading-relaxed max-w-xl">
              <p>
                Byron is a leadership, organisational development, and behaviour change consultant and
                executive coach with over 30 years&rsquo; experience across the NHS, local government,
                wider public services, and the private sector. What has held his interest throughout is
                what happens when people are asked to change how they lead &mdash; and what gets in the way
                when that change doesn&rsquo;t stick.
              </p>
              <p>
                He began his career in local government, leading Birmingham City Council&rsquo;s landmark
                BIG Conversation &mdash; one of the earliest large-scale attempts to properly listen to a
                workforce, not just survey it. He later moved into senior NHS leadership, including Head
                of Communications, and progressed to Head of Inclusion-Improvement at University Hospitals
                Birmingham, one of the UK&rsquo;s largest hospital systems.
              </p>
              <p>
                Alongside this, he holds advisory and non-executive roles with national bodies, and has
                chaired equity and research culture work within one of the country&rsquo;s major biomedical
                research centres.
              </p>
              <p>
                Underpinned by our three core values, Byron works with leaders and organisations in three
                connected ways: as an executive coach, supporting senior leaders through questions of identity,
                decision-making and the systems they lead within; as a consultant, designing and delivering
                culture and leadership programmes for organisations and systems; and as a speaker, sharing
                what this work reveals about the gap between intention and action.
              </p>
              <p>
                What connects all of it is a conviction that lasting change is rarely about willpower or
                awareness alone. It&rsquo;s about what&rsquo;s happening psychologically, emotionally,
                behaviourally and relationally inside people, and inside the systems they work in.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="h-16 md:h-24" aria-hidden="true"></div>

      {/* How I work */}
      <section className="py-24 md:py-32 bg-[#F3EFDF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">How we work</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#1E3A2B] mt-6 leading-[1.02]">
              Diagnosis <span className="italic text-[#6B8E4E]">before</span> prescription.
            </h2>
          </div>
          <div className="md:col-span-8 space-y-8 text-[16px] text-[#3d4a41] leading-relaxed">
            <p>
              We start by listening, not by prescribing. Before any programme is designed, we spend time in
              structured listening and diagnostic inquiry, understanding what&rsquo;s actually happening,
              not just what the brief says. Getting that right determines whether an intervention lands or
              becomes another initiative that fades in six months.
            </p>

            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#E27B47] mb-3">Facilitation style</div>
              <p>
                We work from an inside-out philosophy: the people in the room already hold the knowledge and
                the answers, our job is to create the conditions for them to find it, not hand down a
                solution. We pull deliberately for the full range of voices, including dissenting ones, drawing
                on our experience as an inclusion leader and our own neurodivergence, which often takes us toward
                intuitive routes into a problem when the obvious one isn&rsquo;t working.
              </p>
              <p className="mt-4">
                We hold humility alongside directness, genuinely open to challenge ourselves, while willing
                to name the harder thing when the moment calls for it. As a CQ-certified facilitator and
                qualified Action Learning Set Facilitator, we use both structures to hold that balance,
                rather than leaving it to instinct.
              </p>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#E27B47] mb-3">Contracting and engagement</div>
              <p>
                Every piece of work starts with diagnosis, then a clear proposal, scope, format,
                evaluation, contracted directly with sponsors, because leadership commitment to the
                process is usually what makes change stick. Work happens at whatever level the problem
                actually sits at: individual, team, organisation or system.
              </p>
            </div>

            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#E27B47] mb-3">Handling resistance</div>
              <p>
                Resistance is data, not an obstacle. We hold enough safety for honesty and enough directness
                that a room doesn&rsquo;t quietly agree to avoid the harder conversation. The same balance
                that has held in cultural reviews, grievances and leadership work across large NHS
                workforces, and organisational restructuring and merger processes where people are
                defending far more than a viewpoint.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reading list */}
      <section className="pt-24 md:pt-32 pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#6B8E4E]">On our shelf</div>
            <h2 className="font-serif-display text-5xl md:text-6xl text-[#1E3A2B] mt-6 leading-[1.02]">
              A few books that shape the work.
            </h2>
          </div>
          <ul className="md:col-span-7 divide-y divide-[#E7DFCF] border-t border-b border-[#E7DFCF]">
            {readingList.map((r) => (
              <li key={r} className="py-5 flex items-center justify-between gap-6">
                <span className="text-[17px] text-[#1E3A2B]">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </main>
  );
};

export default About;
